import { useState, useCallback } from 'react';
import { fetchClientFitnessProfile } from 'src/api/queries/useGetClientFitnessProfile';
import { useProgramGeneratorProgressStore } from 'src/store/programGeneratorProgressStore';
import { ProgramStepId } from 'src/screens/OnboardingTrainer/screens/Dashboard/screens/ProgramGeneratorAI/types';
import { resolveBodyProfile } from 'src/utils/genderUtils';

export const useCheckClientFitnessProfile = () => {
  const [isChecking, setIsChecking] = useState(false);

  const checkProfile = useCallback(async (clientId: string): Promise<number> => {
    setIsChecking(true);
    try {
      const profile = await fetchClientFitnessProfile(clientId);
      let nextStep = 3;
      if (profile) {
        const store = useProgramGeneratorProgressStore.getState();
        store.setClientId(clientId);
        if (profile.body_profile || profile.gender) {
          const bp = resolveBodyProfile(profile);
          store.updateStepProgress(ProgramStepId.ClientBodyProfile, [{ slug: bp, value: bp }]);
        }
        if (profile.name) {
          const fullName = `${profile.name} ${profile.surname || ''}`.trim();
          store.updateStepProgress(ProgramStepId.ClientName, [{ slug: fullName, value: fullName }]);
        }
        if (profile.energy_level)
          store.updateStepProgress(ProgramStepId.EnergyLevel, profile.energy_level);
        if (profile.activity_level)
          store.updateStepProgress(ProgramStepId.ActivityLevel, profile.activity_level);
        if (profile.fitness_level)
          store.updateStepProgress(ProgramStepId.FitnessLevel, profile.fitness_level);
        if (profile.movement_insights)
          store.updateStepProgress(ProgramStepId.MovementInsights, profile.movement_insights);
        if (profile.injuries_parts)
          store.updateStepProgress(ProgramStepId.InjuriesParts, profile.injuries_parts);
        if (profile.injuries_notes !== undefined)
          store.updateStepProgress(ProgramStepId.InjuriesNotes, profile.injuries_notes);

        let hasMobility = false;
        let hasStability = false;
        let hasBalance = false;
        let hasSymmetry = false;
        let hasQuality = false;

        const movementObj: Record<string, string> = {};
        if (profile.movement_insights) {
          if (Array.isArray(profile.movement_insights)) {
            if (
              profile.movement_insights.length >= 5 &&
              profile.movement_insights.every((i: any) => typeof i === 'string')
            ) {
              hasMobility = true;
              hasStability = true;
              hasBalance = true;
              hasSymmetry = true;
              hasQuality = true;
            } else {
              profile.movement_insights.forEach((item: any) => {
                if (item && typeof item === 'object' && item.slug) {
                  movementObj[item.slug] = String(item.value ?? '');
                }
              });
            }
          } else if (typeof profile.movement_insights === 'object') {
            Object.keys(profile.movement_insights).forEach((key) => {
              movementObj[key] = String(profile.movement_insights[key] ?? '');
            });
          }
        }

        if (Object.keys(movementObj).length > 0) {
          hasMobility = !!movementObj['mobility-level'] || !!movementObj['mobility_level'];
          hasStability = !!movementObj['stability-control'] || !!movementObj['stability_control'];
          hasBalance = !!movementObj['balance-consistency'] || !!movementObj['balance_consistency'];
          hasSymmetry = !!movementObj['movement-symmetry'] || !!movementObj['movement_symmetry'];
          hasQuality =
            !!movementObj['overall-movement-quality'] || !!movementObj['overall_movement_quality'];
        }

        const isComplete =
          !!profile.energy_level &&
          profile.activity_level !== undefined &&
          !!profile.fitness_level &&
          hasMobility &&
          hasStability &&
          hasBalance &&
          hasSymmetry &&
          hasQuality &&
          profile.injuries_parts !== undefined;

        if (isComplete) {
          nextStep = 8;
        } else if (!profile.energy_level) {
          nextStep = 3;
        } else if (!profile.activity_level) {
          nextStep = 4;
        } else if (!profile.fitness_level) {
          nextStep = 5;
        } else if (!hasMobility || !hasStability || !hasBalance || !hasSymmetry || !hasQuality) {
          nextStep = 6;
        } else if (!profile.injuries_parts) {
          nextStep = 7;
        }
      }
      return nextStep;
    } catch (err) {
      console.error('Failed to fetch fitness profile:', err);
      return 3;
    } finally {
      setIsChecking(false);
    }
  }, []);

  return { checkProfile, isChecking };
};
